import React, { useState } from 'react';
import { Building2, Globe, MapPin, Map, Hash, Plus, X, Tag, User, Phone, Star, Copy, ClipboardCopy, Truck } from 'lucide-react';
import { AddressAutocomplete } from '../../ui/AddressAutocomplete';
import { streetLineFromPlace } from '../../../lib/utils/addressFromPlace';
import { PropRow } from '../../shared/activity-drawer/ui/PropRow';
import { COUNTRY_NAMES, adminDivisionLabel, postalCodeLabel, isItalyName } from '../../../lib/utils/countries';
import { PropSelectRow, PropTextRow } from './rows';

export interface CopySource {
  label: string;
  data: Record<string, any>;
}

interface OperationalLocationsRowsProps {
  locations: any[];
  onChange: (locations: any[]) => void;
  /** operational = Sedi operative (come prima); shipping = Indirizzi di spedizione (destinatario, telefono, predefinito) */
  variant?: 'operational' | 'shipping';
  /** Sedi da cui creare una nuova riga già compilata (sede legale, sedi operative, ...) */
  copySources?: CopySource[];
}

const LABELS = {
  operational: { one: 'Sede operativa', add: 'Aggiungi sede operativa', new: 'Nuova sede operativa', edit: 'Modifica sede operativa', namePh: 'Es. Stabilimento, Ufficio Milano' },
  shipping: { one: 'Indirizzo di spedizione', add: 'Aggiungi indirizzo di spedizione', new: 'Nuovo indirizzo di spedizione', edit: 'Modifica indirizzo di spedizione', namePh: 'Es. Magazzino Nord, Negozio centro' },
} as const;

const rowIcon = (Icon: React.ComponentType<any>) => <Icon className="h-3 w-3" strokeWidth={1.5} />;

const EMPTY_LOCATION = {
  name: '', country: 'Italia', address: '', city: '', province: '',
  postal_code: '', region: '', is_active: true, notes: '',
  recipient_name: '', phone: '', is_default: false,
};

/**
 * Lista di sedi in stile Notion: righe cliccabili + form inline a righe di
 * proprietà (stesso pattern dei referenti). Usata due volte nel drawer:
 * variant "operational" = Sedi operative (come già prima), variant "shipping" =
 * Indirizzi di spedizione (Sei Farm, 02/09/2026: destinatario merce, telefono di
 * consegna, un solo predefinito proposto su ordini e DDT). Entrambe scrivono
 * nella stessa tabella client_operational_locations con location_type diverso.
 */
export function OperationalLocationsRows({ locations, onChange, variant = 'operational', copySources = [] }: OperationalLocationsRowsProps) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  // Riga vuota: prima veniva chiusa senza aggiungere nulla e senza avvisare
  const [emptyRowError, setEmptyRowError] = useState(false);
  const [draft, setDraft] = useState<Record<string, any>>(EMPTY_LOCATION);
  const L = LABELS[variant];
  const isShipping = variant === 'shipping';
  const RowIcon = isShipping ? Truck : Building2;

  const openNew = () => { setDraft({ ...EMPTY_LOCATION, location_type: variant }); setEditingIdx(-1); };
  const sources = copySources.filter((c) => c.data?.address || c.data?.city);
  /** Nuova riga precompilata da una sede (legale, operativa, ...) */
  const openFrom = (name: string, src?: Record<string, any>) => {
    setDraft({
      ...EMPTY_LOCATION,
      location_type: variant,
      name,
      country: src?.country || 'Italia',
      address: src?.address || '',
      city: src?.city || '',
      province: src?.province || '',
      postal_code: src?.postal_code || '',
      region: src?.region || '',
      is_default: isShipping && locations.length === 0,
    });
    setEditingIdx(-1);
  };
  /** Nuovo indirizzo copiato da uno già inserito (senza id: sarà una riga nuova) */
  const duplicate = (idx: number) => {
    const { id: _id, ...src } = locations[idx] || {};
    setDraft({ ...EMPTY_LOCATION, ...src, location_type: variant, name: src.name ? `${src.name} (copia)` : '', is_default: false });
    setEditingIdx(-1);
  };
  const openEdit = (idx: number) => { setDraft({ ...EMPTY_LOCATION, ...locations[idx] }); setEditingIdx(idx); };
  const set = (k: string) => (v: any) => setDraft((p) => ({ ...p, [k]: v }));

  const save = () => {
    if (!draft.name.trim() && !draft.address.trim() && !draft.city.trim()) {
      setEmptyRowError(true);
      return;
    }
    setEmptyRowError(false);
    // Un solo predefinito per cliente
    const base = draft.is_default ? locations.map((l) => ({ ...l, is_default: false })) : locations;
    const row = { ...draft, location_type: variant };
    const next = [...base];
    if (editingIdx === -1) next.push(row);
    else if (editingIdx !== null) next[editingIdx] = row;
    onChange(next);
    setEditingIdx(null);
  };

  const remove = (idx: number) => {
    onChange(locations.filter((_, i) => i !== idx));
    if (editingIdx === idx) setEditingIdx(null);
  };

  const handlePlace = (details: any) => {
    setDraft((p) => ({
      ...p,
      address: streetLineFromPlace(details) || p.address,
      city: details?.city || p.city,
      province: details?.province || p.province,
      postal_code: details?.postal_code || p.postal_code,
      region: details?.region || p.region,
    }));
  };

  return (
    <div>
      {locations.map((loc, idx) => (
        <div
          key={idx}
          className="group flex min-h-[30px] cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/[0.06]"
          onClick={() => openEdit(idx)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') openEdit(idx); }}
        >
          <RowIcon className="h-3 w-3 flex-shrink-0 text-[#787774] dark:text-gray-400" strokeWidth={1.5} />
          <span className="min-w-0 flex-1 truncate text-[13px] text-[#37352F] dark:text-white">
            {loc.name || L.one}
            {(loc.city || loc.country) && (
              <span className="text-[rgba(55,53,47,0.5)] dark:text-gray-400">
                {' '}· {[loc.address, loc.city].filter(Boolean).join(', ') || loc.country}
              </span>
            )}
          </span>
          {loc.is_default && (
            <span className="inline-flex flex-shrink-0 items-center gap-0.5 rounded bg-[#2383E2]/10 px-1.5 py-0.5 text-[10px] font-medium text-[#2383E2]" title="Indirizzo di spedizione predefinito">
              <Star className="h-2.5 w-2.5" /> Predefinito
            </span>
          )}
          <button
            type="button"
            aria-label="Duplica"
            title="Crea una nuova riga copiando questa"
            onClick={(e) => { e.stopPropagation(); duplicate(idx); }}
            className="opacity-0 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
          >
            <Copy className="h-3.5 w-3.5 text-[#787774] hover:text-[#37352F] dark:hover:text-white" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            aria-label="Rimuovi"
            onClick={(e) => { e.stopPropagation(); remove(idx); }}
            className="opacity-0 hover:text-red-500 group-hover:opacity-100 [@media(hover:none)]:opacity-100"
          >
            <X className="h-3.5 w-3.5 text-[#787774]" strokeWidth={1.5} />
          </button>
        </div>
      ))}

      {editingIdx === null ? (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-1">
          <button
            type="button"
            onClick={openNew}
            className="flex items-center gap-1.5 rounded px-1.5 py-1 text-[13px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
            {L.add}
          </button>
          {sources.length > 0 && (
            <label className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 text-[13px] text-[#787774] hover:bg-[rgba(55,53,47,0.08)] dark:text-gray-400 dark:hover:bg-white/[0.06]">
              <ClipboardCopy className="h-3.5 w-3.5" strokeWidth={1.5} />
              <select
                value=""
                aria-label="Copia da una sede"
                onChange={(e) => {
                  const src = sources[Number(e.target.value)];
                  if (src) openFrom(src.label, src.data);
                }}
                className="cursor-pointer bg-transparent text-[13px] text-[#787774] outline-none dark:text-gray-400"
              >
                <option value="">Copia da…</option>
                {sources.map((c, i) => (
                  <option key={i} value={i}>{c.label}</option>
                ))}
              </select>
            </label>
          )}
        </div>
      ) : (
        <div className="mt-1 rounded-md border border-[rgba(55,53,47,0.09)] px-2 py-1.5 dark:border-white/10">
          <p className="mb-1 px-1.5 text-[11px] font-medium uppercase tracking-wide text-[rgba(55,53,47,0.5)] dark:text-gray-400">
            {editingIdx === -1 ? L.new : L.edit}
          </p>
          <PropTextRow icon={rowIcon(Tag)} label="Nome" value={draft.name} onChange={set('name')} placeholder={L.namePh} />
          <PropSelectRow
            icon={rowIcon(Globe)}
            label="Paese"
            value={draft.country}
            onChange={set('country')}
            options={COUNTRY_NAMES.map((n) => ({ value: n, label: n }))}
            placeholder="Seleziona paese..."
          />
          <PropRow icon={rowIcon(MapPin)} label="Indirizzo">
            <div className="min-w-0 flex-1">
              <AddressAutocomplete
                label=""
                bare
                value={draft.address}
                onChange={set('address')}
                onPlaceSelected={handlePlace}
                country={draft.country || ''}
                placeholder="Inizia a digitare l'indirizzo..."
              />
            </div>
          </PropRow>
          <PropTextRow icon={rowIcon(Building2)} label="Città" value={draft.city} onChange={set('city')} />
          <PropTextRow icon={rowIcon(MapPin)} label={adminDivisionLabel(draft.country)} value={draft.province} onChange={set('province')} />
          <PropTextRow icon={rowIcon(Hash)} label={postalCodeLabel(draft.country)} value={draft.postal_code} onChange={set('postal_code')} />
          {(isItalyName(draft.country) || draft.region) && (
            <PropTextRow icon={rowIcon(Map)} label="Regione" value={draft.region} onChange={set('region')} />
          )}
          {isShipping && (<>
          <PropTextRow icon={rowIcon(User)} label="Destinatario" value={draft.recipient_name || ''} onChange={set('recipient_name')} placeholder="Se diverso dalla ragione sociale" />
          <PropTextRow icon={rowIcon(Phone)} label="Tel. consegna" value={draft.phone || ''} onChange={set('phone')} placeholder="Es. +39 333 1234567" />
          <PropRow icon={rowIcon(Star)} label="Predefinito">
            <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-[13px] text-[#37352F] dark:text-white">
              <input
                type="checkbox"
                checked={!!draft.is_default}
                onChange={(e) => set('is_default')(e.target.checked)}
                className="rounded border-gray-300 text-[#2383E2] focus:ring-[#2383E2]"
              />
              <span className="text-[rgba(55,53,47,0.5)] dark:text-gray-400">proposto per primo su ordini e DDT</span>
            </label>
          </PropRow>
          </>)}
          {emptyRowError && (
            <p className="px-1.5 pt-1 text-[12px] text-red-600 dark:text-red-400">
              Compila almeno nome, indirizzo o città: così com'è la riga non viene aggiunta.
            </p>
          )}
          <div className="mt-1 flex justify-end gap-2 px-1.5 pb-0.5">
            <button
              type="button"
              onClick={() => { setEmptyRowError(false); setEditingIdx(null); }}
              className="rounded px-2 py-1 text-[12.5px] font-medium text-[#37352F] hover:bg-[rgba(55,53,47,0.08)] dark:text-white dark:hover:bg-white/[0.06]"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={save}
              className="rounded bg-[#37352F] px-2.5 py-1 text-[12.5px] font-semibold text-white hover:opacity-90 dark:bg-white dark:text-[#191919]"
            >
              {editingIdx === -1 ? 'Aggiungi' : 'Salva'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
